import React, { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { Button, Input } from 'antd';
import { Spin } from 'common/components';
import { debounce, onInputKeyDown } from 'common/helpers';
import { Close, Search } from 'common/svg';
import { CHARACTER_LIMIT_MIN, DEFAULT_VALUE_0 } from 'common/config';

interface IComponentProps {
  auto?: boolean;
  placeholder: string;
  minLength?: number;
  clearDeps?: any[];
  onSearch: (value?: string) => Promise<void>;
}

export const SearchInput: FC<IComponentProps> = (props) => {
  const { auto, placeholder, clearDeps = [], minLength = CHARACTER_LIMIT_MIN, onSearch } = props;

  const [value, setValue] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const debouncedChange = useMemo(
    () =>
      debounce(async (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        setLoading(true);
        value.trim().length >= minLength ? await onSearch(value) : await onSearch(undefined);
        setLoading(false);
      }),
    [onSearch],
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setValue(value);

    if (auto && (value.trim().length === DEFAULT_VALUE_0 || value.trim().length >= minLength)) {
      debouncedChange(e);
    }
  };

  const handleSearch = async () => {
    if (loading) {
      return;
    }

    setLoading(true);
    value.trim().length >= minLength ? await onSearch(value) : await onSearch(undefined);
    setLoading(false);
  };

  const handleClear = async () => {
    if (loading) {
      return;
    }

    setValue('');
    setLoading(true);

    if (value.trim().length >= minLength) {
      await onSearch();
    }

    setLoading(false);
  };

  useEffect(() => {
    setValue('');
  }, [...clearDeps]);

  return (
    <div className="flex align-center flex-1">
      <Input
        prefix={loading ? <Spin small /> : <Search className="icon-search-dark-grey" />}
        placeholder={placeholder}
        allowClear={{
          clearIcon: (
            <Close
              className="icon-close-input"
              onClick={(e) => {
                if (auto && !loading) {
                  return;
                }

                e.stopPropagation();
                handleClear();
              }}
            />
          ),
        }}
        value={value}
        onChange={handleChange}
        onKeyDown={(e) => {
          if (!auto) {
            onInputKeyDown(e, handleSearch);
          }
        }}
      />

      {!auto && (
        <div className="ml-4">
          <Button
            className="button-circle primary"
            icon={<Search className="icon-search-white" />}
            onClick={handleSearch}
            loading={loading}
          />
        </div>
      )}
    </div>
  );
};
